import { useQuery } from "@tanstack/react-query";
import { Gauge, X } from "lucide-react";
import { getSeatDetail } from "@/services/channelCenter";
import type { SeatDetailRow } from "@/services/types/channelCenter";

const BOTTLENECK_CN: Record<string, string> = { schedule: "团期名额", vehicle: "车位", hotel: "酒店", ticket: "门票" };

/** 一格：数字（null = 不约束，显示「—」）；是短板的那一格标红加粗。 */
function Cell({ n, tight, unrecorded }: { n: number | null; tight: boolean; unrecorded?: boolean }) {
  return (
    <td className={`tabular-nums ${tight ? "font-bold text-rose-600" : "text-slate-700"}`}>
      {n == null ? <span className="text-slate-300">—</span> : n}
      {unrecorded && <span className="ml-1 rounded bg-amber-50 px-1 text-[10.5px] font-medium text-amber-700">未录</span>}
    </td>
  );
}

function unrecordedTitle(r: SeatDetailRow): string {
  const hotels = r.hotel_unrecorded.map((u) => `${u.name || "酒店"} ${u.night}`);
  const tickets = r.ticket_unrecorded.map((u) => `${u.name || "门票"} ${u.date}`);
  return [...hotels.map((s) => `酒店未录：${s}`), ...tickets.map((s) => `门票未录：${s}`)].join("\n");
}

/**
 * CHANAV-3 · 上架中心「余位明细」：这个产品在本平台上对外报的渠道余位，每天 = min(团期名额, 车位, 酒店, 门票)，
 * 标出短板和「酒店未录 / 门票未录」。只读。总开关关着时只有团期名额 + 车位两项，并提示酒店、门票未计入。
 */
export function SeatDetailModal({ platformCode, productId, productName, productCode, onClose }: {
  platformCode: string; productId: string; productName: string; productCode: string; onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["channel-center", "seat-detail", platformCode, productId],
    queryFn: () => getSeatDetail(platformCode, productId, 30),
  });
  const on = q.data?.include_hotel_ticket ?? false;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div role="dialog" data-testid="seat-detail-modal" className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Gauge size={18} className="text-blue-600" /> 余位明细 · {productName}</h2>
          <button type="button" aria-label="关闭" onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <p className="mt-1 text-[12px] text-slate-500">{productCode} · 今天起 30 天里有团期的日子 · 平台上看到的数 = 下面{on ? "四" : "两"}项里最小的那个（红色 = 短板）{q.data?.occupancy ? ` · 按 ${q.data.occupancy} 人一间` : ""}</p>

        {q.isLoading && <div className="p-8 text-center text-[13px] text-slate-500">加载中...</div>}
        {q.isError && (
          <button type="button" onClick={() => q.refetch()} className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">加载失败，点击重试</button>
        )}
        {q.data && (
          <>
            {!on && (
              <p data-testid="seat-detail-off" className="mt-3 rounded-lg bg-slate-100 p-2.5 text-[12.5px] text-slate-600">酒店、门票未计入（开关关闭）——渠道余位目前只看团期名额和车位。老板 / 技术管理员可在「系统设置 › 通用设置 › 渠道余位」里打开。</p>
            )}
            {q.data.rows.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-slate-400">未来 30 天没有团期</div>
            ) : (
              <div className="mt-3 overflow-y-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
                      <th className="py-2">出发日</th><th>渠道余位</th><th>团期名额</th><th>车位</th>
                      {on && <><th>酒店</th><th>门票</th></>}
                      <th>短板</th>
                    </tr>
                  </thead>
                  <tbody data-testid="seat-detail-rows">
                    {q.data.rows.map((r) => (
                      <tr key={r.date} className="border-b border-slate-100" data-testid={`seat-row-${r.date}`}>
                        <td className="py-2 tabular-nums">{r.date}</td>
                        <td className={`tabular-nums font-semibold ${r.seats === 0 ? "text-rose-600" : "text-slate-900"}`}>{r.seats}</td>
                        <Cell n={r.schedule_left} tight={r.bottleneck === "schedule"} />
                        <Cell n={r.vehicle_seats} tight={r.bottleneck === "vehicle"} />
                        {on && <Cell n={r.hotel_seats} tight={r.bottleneck === "hotel"} unrecorded={r.hotel_unrecorded.length > 0} />}
                        {on && <Cell n={r.ticket_seats} tight={r.bottleneck === "ticket"} unrecorded={r.ticket_unrecorded.length > 0} />}
                        <td className="text-[12.5px] text-slate-600" title={unrecordedTitle(r) || undefined}>
                          {r.source === "unsellable" ? "不可售" : r.bottleneck ? BOTTLENECK_CN[r.bottleneck] ?? r.bottleneck : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {on && <p className="mt-2 text-[11.5px] text-slate-400">「未录」= 这家酒店这几晚 / 这张票这天一条库存都没录，按不约束算；录了但是 0 = 满，渠道余位就是 0。鼠标停在短板上能看到具体哪家酒店哪一晚没录。</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
